import React, { useRef, useState } from "react";
import { CaseIcon, ChevronDownIcon } from "../../../icons";
import {
  Box,
  Button,
  Divider,
  Popover,
  styled,
  Typography,
  useTheme,
} from "@material-ui/core";
import { ChipFocusColor, ChipHoverColor, DefaultCrumb } from "../Breadcrumbs";
import { PRIMARY_BLUE } from "../../../theme";
import { BreadcrumbItem } from "../BreadcrumbsProvider";
import { useAccountCustomer } from "../../../api/hooks/useAccountCustomer";
import { useParams } from "react-router-dom";
import { useCustomerAccounts } from "../hooks/useCustomerAccounts";
import { AccountSnippetRow } from "./AccountSnippetRow";
import { PageLink } from "../../CustomLink";
import { AlertBlock } from "../../AlertBlock";
import { DialogLoader } from "../../Loader";

const StyledButton = styled(Button)(({ theme }) => ({
  textDecoration: "none",
  color: theme.palette.primary.main,
  minWidth: "0px",
  width: "26px",
  height: "26px",
  lineHeight: 1,
  backgroundColor: PRIMARY_BLUE["50"],
  "&:hover": {
    backgroundColor: ChipHoverColor,
  },
  "&:focus": {
    backgroundColor: ChipFocusColor,
    outline: "none",
  },
}));

export const AccountCrumbChip: React.FC<BreadcrumbItem> = (item) => {
  const { accountNumber } = useParams();
  const theme = useTheme();
  const { data: customer, error: customerError } = useAccountCustomer(
    accountNumber,
  );
  const {
    data: accounts,
    error: accountsError,
    isLoading,
  } = useCustomerAccounts(customer?.number);

  const crumbRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const handleOpen = () => {
    setAnchorEl(crumbRef?.current || null);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  // For one account don't display dropdown menu
  return customer && customer.numberOfAccounts === "1" ? (
    <DefaultCrumb {...item} />
  ) : (
    <Box display="flex" data-testid="AccountCrumbChip__Dropdown">
      <Box {...{ ref: crumbRef }} mr={0.125}>
        <DefaultCrumb {...item} />
      </Box>
      <StyledButton onClick={handleOpen} data-testid="AccountCrumbChip__Button">
        <ChevronDownIcon
          width={16}
          height={16}
          fill={theme.palette.primary.main}
        />
      </StyledButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        disableAutoFocus
      >
        {customerError || accountsError ? (
          <Box px={2}>
            <AlertBlock
              variant="error"
              isClosable={false}
              messages={[
                "We can't load other accounts for this customer at the moment.",
                "Please try again later.",
              ]}
            />
          </Box>
        ) : isLoading ? (
          <DialogLoader p={2} />
        ) : customer && customer.numberOfAccounts < 6 ? (
          (accounts || []).map((account) => {
            return (
              <AccountSnippetRow
                {...account}
                autofocus={accountNumber === account.number}
                handleSelect={handleClose}
                key={account.number}
              />
            );
          })
        ) : customer ? (
          <>
            <Box
              display="flex"
              p={2}
              data-testid="AccountCrumbChip__Popover--search"
            >
              <Box mr={1} mt={0.5} clone>
                <CaseIcon
                  height={16}
                  width={16}
                  fill={theme?.palette.text.primary}
                />
              </Box>
              <Box>
                <Typography variant="body1">{`${customer.numberOfAccounts} accounts`}</Typography>
                <Typography variant="body2" color="textSecondary">
                  Please use the account search to access other accounts of this
                  customer
                </Typography>
              </Box>
            </Box>
            <Divider />
            <Box px={2} py={1.5}>
              <PageLink text="Account search" path="/" />
            </Box>
          </>
        ) : null}
      </Popover>
    </Box>
  );
};
